import React, { useState, useEffect, useRef } from 'react';
import { Mail, Phone, MapPin, Send, ChevronDown } from 'lucide-react';

// Country data with flags, codes, and dial codes
// Using flagcdn.com for flag images
const countries = [
  { code: 'IN', name: 'India', dialCode: '+91', flag: 'https://flagcdn.com/w40/in.png' },
  { code: 'US', name: 'United States', dialCode: '+1', flag: 'https://flagcdn.com/w40/us.png' },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', flag: 'https://flagcdn.com/w40/gb.png' },
  { code: 'AE', name: 'UAE', dialCode: '+971', flag: 'https://flagcdn.com/w40/ae.png' },
  { code: 'SG', name: 'Singapore', dialCode: '+65', flag: 'https://flagcdn.com/w40/sg.png' },
  { code: 'AU', name: 'Australia', dialCode: '+61', flag: 'https://flagcdn.com/w40/au.png' },
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: 'https://flagcdn.com/w40/ca.png' },
  { code: 'DE', name: 'Germany', dialCode: '+49', flag: 'https://flagcdn.com/w40/de.png' },
  { code: 'FR', name: 'France', dialCode: '+33', flag: 'https://flagcdn.com/w40/fr.png' },
  { code: 'JP', name: 'Japan', dialCode: '+81', flag: 'https://flagcdn.com/w40/jp.png' },
  { code: 'NL', name: 'Netherlands', dialCode: '+31', flag: 'https://flagcdn.com/w40/nl.png' },
  { code: 'SA', name: 'Saudi Arabia', dialCode: '+966', flag: 'https://flagcdn.com/w40/sa.png' },
  { code: 'QA', name: 'Qatar', dialCode: '+974', flag: 'https://flagcdn.com/w40/qa.png' },
  { code: 'KW', name: 'Kuwait', dialCode: '+965', flag: 'https://flagcdn.com/w40/kw.png' },
  { code: 'OM', name: 'Oman', dialCode: '+968', flag: 'https://flagcdn.com/w40/om.png' },
  { code: 'BH', name: 'Bahrain', dialCode: '+973', flag: 'https://flagcdn.com/w40/bh.png' },
  { code: 'NZ', name: 'New Zealand', dialCode: '+64', flag: 'https://flagcdn.com/w40/nz.png' },
  { code: 'ZA', name: 'South Africa', dialCode: '+27', flag: 'https://flagcdn.com/w40/za.png' },
];

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: 'IN',
    phone: '',
    company: '',
    service: '',
    budget: '',
    timeline: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const countryDropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setIsCountryDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const selectedCountry = countries.find(c => c.code === formData.countryCode) || countries[0];

  const serviceOptions = [
    { value: '', label: 'Select a service' },
    { value: 'new-website', label: 'New Website' },
    { value: 'website-redesign', label: 'Website Redesign' },
    { value: 'web-application', label: 'Web Application' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'something-else', label: 'Something Else' }
  ];

  const budgetOptions = [
    { value: '', label: 'Select budget' },
    { value: '10k-25k', label: '₹10k – ₹25k' },
    { value: '25k-50k', label: '₹25k – ₹50k' },
    { value: '50k-1L', label: '₹50k – ₹1L' },
    { value: '1L+', label: '₹1L+' }
  ];

  const timelineOptions = [
    { value: '', label: 'Select timeline' },
    { value: 'immediately', label: 'Immediately' },
    { value: 'within-1-month', label: 'Within 1 month' },
    { value: '1-3-months', label: '1–3 months' },
    { value: 'just-exploring', label: 'Just exploring' }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Please enter your name';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Please enter your email';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Please enter your phone number';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }

    if (!formData.service) {
      newErrors.service = 'Please select a service';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Please tell us about your project';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // For phone, only allow digits
    if (name === 'phone') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, [name]: digitsOnly }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear error on input
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCountrySelect = (countryCode) => {
    setFormData(prev => ({ ...prev, countryCode }));
    setIsCountryDropdownOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          access_key: 'f1ecc481-73fa-4fbb-9907-ef6fb97e0fd3',
          botcheck: '',
          name: formData.name,
          email: formData.email,
          country: selectedCountry.name,
          country_code: selectedCountry.code,
          dial_code: selectedCountry.dialCode,
          phone: `${selectedCountry.dialCode} ${formData.phone}`,
          company: formData.company || 'Not provided',
          service: serviceOptions.find(s => s.value === formData.service)?.label || formData.service,
          budget: budgetOptions.find(b => b.value === formData.budget)?.label || formData.budget || 'Not specified',
          timeline: timelineOptions.find(t => t.value === formData.timeline)?.label || formData.timeline || 'Not specified',
          message: formData.message,
          subject: `Arvix Contact: ${serviceOptions.find(s => s.value === formData.service)?.label || 'New Inquiry'}`
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          countryCode: 'IN',
          phone: '',
          company: '',
          service: '',
          budget: '',
          timeline: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <Mail size={22} />,
      title: 'Email',
      content: 'info@arvixconsulting.in',
      href: 'mailto:info@arvixconsulting.in'
    },
    {
      icon: <Phone size={22} />,
      title: 'Phone',
      content: '+91 97668 75553',
      href: 'tel:+919766875553'
    },
    {
      icon: <MapPin size={22} />,
      title: 'Office',
      content: 'Pune-Alandi Road, Dighi, India',
      href: null
    }
  ];

  return (
    <>
      {/* Page Header */}
      <section className="page-header" data-testid="contact-header">
        <div className="container">
          <h1>Contact Us</h1>
          <p>
            Let's discuss how we can help your business succeed.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section" data-testid="contact-section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Information */}
            <div className="contact-info">
              <h2>Get in Touch</h2>
              <p>
                We'd love to hear from you. Whether you have a question about our services, 
                need a consultation, or want to discuss a project, we're here to help.
              </p>
              
              <div className="contact-details">
                {contactInfo.map((item, index) => (
                  <div key={index} className="contact-item" data-testid={`contact-${item.title.toLowerCase()}`}>
                    <div className="contact-item-icon">{item.icon}</div>
                    <div className="contact-item-text">
                      <h4>{item.title}</h4>
                      {item.href ? (
                        <p><a href={item.href}>{item.content}</a></p>
                      ) : (
                        <p>{item.content}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="contact-hours">
                <h4>Business Hours</h4>
                <p className="text-muted">
                  Monday - Friday: 9:00 AM - 6:00 PM IST<br />
                  Saturday: 10:00 AM - 2:00 PM IST<br />
                  Sunday: Closed
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-wrapper">
              <h3>Tell Us About Your Project</h3>
              <form onSubmit={handleSubmit} noValidate data-testid="contact-form">
                {/* Honeypot/Botcheck for spam protection */}
                <input type="checkbox" name="botcheck" style={{ display: 'none' }} />
                
                {/* Basic Info Section */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`form-input ${errors.name ? 'error' : ''}`}
                      placeholder="John Doe"
                      data-testid="contact-name-input"
                    />
                    {errors.name && <span className="form-error">{errors.name}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`form-input ${errors.email ? 'error' : ''}`}
                      placeholder="john@example.com"
                      data-testid="contact-email-input"
                    />
                    {errors.email && <span className="form-error">{errors.email}</span>}
                  </div>
                </div>

                <div className="form-row">
                  {/* Phone with Country Code */}
                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">Phone Number *</label>
                    <div className="phone-input-group">
                      <div className="country-select-wrapper" ref={countryDropdownRef}>
                        <button
                          type="button"
                          className="country-select-btn"
                          onClick={() => setIsCountryDropdownOpen(!isCountryDropdownOpen)}
                          data-testid="country-select-btn"
                        >
                          <img src={selectedCountry.flag} alt={selectedCountry.name} className="country-flag-img" />
                          <span className="country-dial-code">{selectedCountry.dialCode}</span>
                          <ChevronDown size={14} />
                        </button>
                        {isCountryDropdownOpen && (
                          <div className="country-dropdown">
                            {countries.map((country) => (
                              <button
                                key={country.code}
                                type="button"
                                className={`country-option ${country.code === formData.countryCode ? 'selected' : ''}`}
                                onClick={() => handleCountrySelect(country.code)}
                                data-testid={`country-option-${country.code}`}
                              >
                                <img src={country.flag} alt={country.name} className="country-flag-img" />
                                <span className="country-name">{country.name}</span>
                                <span className="country-dial-code">{country.dialCode}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className={`form-input phone-input ${errors.phone ? 'error' : ''}`}
                        placeholder="9876543210"
                        maxLength={10}
                        data-testid="contact-phone-input"
                      />
                    </div>
                    {errors.phone && <span className="form-error">{errors.phone}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="company" className="form-label">Company / Business Name</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Your Company Name"
                      data-testid="contact-company-input"
                    />
                  </div>
                </div>

                {/* Service Selection */}
                <div className="form-group">
                  <label htmlFor="service" className="form-label">What Service(s) do you need? *</label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className={`form-input form-select ${errors.service ? 'error' : ''}`}
                    data-testid="contact-service-select"
                  >
                    {serviceOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors.service && <span className="form-error">{errors.service}</span>}
                </div>

                <div className="form-row form-row-compact">
                  {/* Budget Selection */}
                  <div className="form-group form-group-sm">
                    <label htmlFor="budget" className="form-label">Estimated Budget</label>
                    <select
                      id="budget"
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      className="form-input form-select"
                      data-testid="contact-budget-select"
                    >
                      {budgetOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Timeline Selection */}
                  <div className="form-group form-group-lg">
                    <label htmlFor="timeline" className="form-label">Project Timeline</label>
                    <select
                      id="timeline"
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleChange}
                      className="form-input form-select"
                      data-testid="contact-timeline-select"
                    >
                      {timelineOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Project Description */}
                <div className="form-group">
                  <label htmlFor="message" className="form-label">Tell us about your project *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className={`form-textarea ${errors.message ? 'error' : ''}`}
                    placeholder="Describe your project goals, requirements, and any specific features you need..."
                    rows={5}
                    data-testid="contact-message-input"
                  />
                  {errors.message && <span className="form-error">{errors.message}</span>}
                </div>

                <button
                  type="submit"
                  className={`btn btn-primary ${isSubmitting ? 'loading' : ''}`}
                  disabled={isSubmitting}
                  style={{ width: '100%' }}
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? 'Sending...' : (
                    <>
                      Send Message <Send size={18} />
                    </>
                  )}
                </button>

                {submitStatus === 'success' && (
                  <div className="form-status success" data-testid="form-success">
                    Message sent successfully! We'll get back to you within 24 hours.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="form-status error" data-testid="form-error">
                    Something went wrong. Please try again or contact us directly at info@arvixconsulting.in
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
